"use client"

import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react"
import { useAuth } from "./AuthContext"
import chatService from "../services/chatService"
import { notification } from "antd"

// Helper function for debouncing
const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

function userRoleIsAdmin(u) {
  if (!u) return false
  const r = u.role || u.user_type || u.userRole
  return r === 'admin'
}

function readAdminFromStorage() {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return false
    const p = JSON.parse(raw)
    const u = p.user || p
    return userRoleIsAdmin(u)
  } catch {
    return false
  }
}

// Valeur par défaut du contexte
const defaultContextValue = {
  conversations: [],
  messages: [],
  isLoading: false,
  currentConversation: null,
  error: null,
  showError: (message) => console.error('ChatContext not initialized:', message),
  setMessages: () => {},
  setConversations: () => {},
  setIsLoading: () => {},
  loadConversations: async () => {},
  fetchConversations: async () => {},
  sendMessage: async () => {},
  markAsRead: async () => {},
  setCurrentConversation: () => {}
}

const ChatContext = createContext(defaultContextValue)

export const ChatProvider = ({ children }) => {
  const { user, token, setUser } = useAuth()
  const [conversations, setConversations] = useState([])
  const [currentConversation, setCurrentConversation] = useState(null)
  const [messages, setMessages] = useState([])
  const [isTyping, setIsTyping] = useState(false)
  const [typingUserId, setTypingUserId] = useState(null)
  const [unreadCount, setUnreadCount] = useState(0)
  const [isConnected, setIsConnected] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [notificationApi, contextHolder] = notification.useNotification()

  // Refs
  const isMounted = useRef(true)
  const initInProgressRef = useRef(false)
  const lastFetchTimeRef = useRef(0)
  const lastDataRef = useRef([])
  const inProgressRef = useRef(false)
  const typingTimeoutRef = useRef(null)
  const cleanupRef = useRef(null)
  const reconnectTimeoutRef = useRef(null)
  const socketInitialized = useRef(false)
  const initializingRef = useRef(false)
  const pendingInitRef = useRef(false)
  const hasConnectedRef = useRef(false)
  const firstRender = useRef(true)

  // Show error notification
  const showError = useCallback(
    (message) => {
      notificationApi.error({
        message: "Erreur",
        description: message,
        placement: "topRight",
      })
    },
    [notificationApi]
  )

  /** ============= MARK AS READ ============= **/
  const markAsRead = useCallback(
    async (conversationId) => {
      if (!conversationId) return
      try {
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
          )
        )

        setMessages((prev) =>
          prev.map((msg) =>
            msg.conversationId === conversationId && !msg.isRead && msg.senderId !== user?.id
              ? { ...msg, isRead: true }
              : msg
          )
        )

        await chatService.markAsRead(conversationId)
      } catch (error) {
        console.error("Error marking as read:", error)
        showError("Impossible de marquer comme lu")
      }
    },
    [user?.id, showError]
  )

  /** ============= LOAD MESSAGES ============= **/
  const loadMessages = useCallback(
    async (conversationId, beforeMessageId = null) => {
      try {
        const newMessages = await chatService.getMessages(conversationId, beforeMessageId)
        if (!newMessages?.length) return []

        setMessages((prev) => {
          const existingIds = new Set(prev.map((m) => m.id))
          const uniqueNewMessages = newMessages.filter((msg) => !existingIds.has(msg.id))
          return beforeMessageId
            ? [...uniqueNewMessages, ...prev]
            : [...uniqueNewMessages, ...prev]
        })

        if (!beforeMessageId && currentConversation?.id === conversationId) {
          markAsRead(conversationId).catch(console.error)
        }

        return newMessages
      } catch (error) {
        console.error("Error loading messages:", error)
        showError("Impossible de charger les messages")
        throw error
      }
    },
    [currentConversation?.id, markAsRead, showError]
  )

  /** ============= HANDLE NEW MESSAGE ============= **/
  const handleNewMessage = useCallback(
    (message) => {
      const isFromCurrentUser = message.senderId === user?.id
      const isInCurrentConversation = currentConversation?.id === message.conversationId

      if (isInCurrentConversation) {
        setMessages((prev) =>
          prev.some((m) => m.id === message.id) ? prev : [message, ...prev]
        )
        if (!isFromCurrentUser) markAsRead(message.conversationId).catch(console.error)
      }

      setConversations((prev) => {
        const updated = [...prev]
        const index = updated.findIndex((c) => c.id === message.conversationId)
        if (index !== -1) {
          updated[index] = {
            ...updated[index],
            lastMessage: message,
            updatedAt: new Date().toISOString(),
            unreadCount: isInCurrentConversation ? 0 : (updated[index].unreadCount || 0) + 1,
          }
          const [moved] = updated.splice(index, 1)
          updated.unshift(moved)
        }
        return updated
      })

      if (!isInCurrentConversation) setUnreadCount((prev) => prev + 1)
    },
    [currentConversation?.id, markAsRead, user?.id]
  )

  /** ============= HANDLE TYPING ============= **/
  const handleTyping = useCallback(
    ({ userId, isTyping: userIsTyping, conversationId }) => {
      if (conversationId !== currentConversation?.id) return
      setIsTyping(userIsTyping)
      setTypingUserId(userIsTyping ? userId : null)
      if (userIsTyping) {
        clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = setTimeout(() => {
          setIsTyping(false)
          setTypingUserId(null)
        }, 3000)
      }
    },
    [currentConversation?.id]
  )

  // Référence pour suivre les messages déjà traités
  const processedMessagesRef = useRef(new Set());
  
  // Effet pour nettoyer les messages traités lorsqu'on change de conversation
  useEffect(() => {
    // Réinitialiser l'ensemble des messages traités quand on change de conversation
    processedMessagesRef.current.clear();
  }, [currentConversation?.id]);

  // Effet pour écouter les messages en temps réel
  useEffect(() => {
    if (!user) return;

    
    // Fonction pour générer une clé unique pour un message
    const getMessageKey = (message) => {
      // Si le message a un ID non temporaire, on l'utilise directement
      if (message.id && !message.id.startsWith('temp-')) {
        return message.id;
      }
      
      // Pour les messages optimistes, on utilise une clé basée sur le contenu et le timestamp
      const content = message.content || '';
      const senderId = message.senderId || '';
      const conversationId = message.conversationId || '';
      const timestamp = message.timestamp || message.createdAt || Date.now();
      
      // Créer une empreinte unique pour le message
      return `msg-${conversationId}-${senderId}-${content.length}-${timestamp}`;
    };
    
    // Écouter les nouveaux messages
    const handleNewMessage = (raw) => {
      try {
        const conversationId =
          raw?.conversationId || raw?.chatId || raw?.message?.chatId || raw?.message?.conversationId
        const msg = raw?.message || raw
        if (!conversationId || !msg) return

        const message = {
          ...msg,
          id: msg.id || msg._id,
          conversationId,
          chatId: conversationId,
          senderId: msg.senderId || msg.sender_id,
          content: msg.content,
          createdAt: msg.createdAt || msg.created_at || new Date().toISOString(),
          timestamp: msg.timestamp || msg.createdAt || msg.created_at,
        }
        
        const isFromCurrentUser = message.senderId === user?.id;
        const messageKey = getMessageKey(message);
        
        // Vérifier si le message a déjà été traité
        if (processedMessagesRef.current.has(messageKey)) {
          return;
        }
        
        // Ajouter le message à l'ensemble des messages traités
        processedMessagesRef.current.add(messageKey);
        
        // Limiter la taille du cache des messages traités
        if (processedMessagesRef.current.size > 200) {
          const values = Array.from(processedMessagesRef.current);
          processedMessagesRef.current = new Set(values.slice(-100));
        }
    
        
        // Mettre à jour la liste des messages
    setMessages(prev => {
      // Vérifier si le message existe déjà dans l'état actuel
      const existingMessageIndex = prev.findIndex(m => {
        // Si les deux messages ont un ID, on compare les IDs
        if (m.id && message.id && m.id === message.id) return true;
        
        // Pour les messages optimistes, on compare le contenu et l'expéditeur
        if (m.isOptimistic && m.content === message.content && m.senderId === message.senderId) {
          return true;
        }
        
        return false;
      });
      
      // Si le message existe déjà, on le met à jour
      if (existingMessageIndex !== -1) {
        const newMessages = [...prev];
        newMessages[existingMessageIndex] = { 
          ...newMessages[existingMessageIndex],
          ...message,
          isOptimistic: false // On s'assure que le message n'est plus marqué comme optimiste
        };
        return newMessages;
      }
      
      // Si c'est un nouveau message, on l'ajoute au début du tableau
      return [message, ...prev];
    });
    
    // Mettre à jour la conversation uniquement si le message n'est pas de l'utilisateur actuel
    // ou si c'est une réponse à un message optimiste
    if (!isFromCurrentUser || message.isOptimistic) {
      setConversations(prev => {
            const updated = [...prev];
            const convIndex = updated.findIndex(c => c.id === message.conversationId);
            
            if (convIndex !== -1) {
              // Ne pas mettre à jour si le dernier message est plus récent
              const lastMessage = updated[convIndex].lastMessage;
              const lastMessageTime = lastMessage ? new Date(lastMessage.createdAt || lastMessage.timestamp).getTime() : 0;
              const newMessageTime = new Date(message.createdAt || message.timestamp || Date.now()).getTime();
              
              if (lastMessage && lastMessageTime > newMessageTime) {
                return prev;
              }
              
              
              // Mettre à jour la conversation existante
              updated[convIndex] = {
                ...updated[convIndex],
                lastMessage: message,
                updatedAt: new Date().toISOString(),
                unreadCount: currentConversation?.id === message.conversationId 
                  ? 0 
                  : (updated[convIndex].unreadCount || 0) + (isFromCurrentUser ? 0 : 1)
              };
              
              // Déplacer la conversation en haut de la liste
              const [moved] = updated.splice(convIndex, 1);
              updated.unshift(moved);
            }
            
            return updated;
          });
        }
      } catch (error) {
        console.error('[ChatContext] Erreur lors du traitement du message:', error);
      }
    };
    
    // S'abonner aux événements
    const unsubscribeMessage = chatService.on('message', handleNewMessage);
    
    // Nettoyage
    return () => {
      if (unsubscribeMessage) unsubscribeMessage();
    };
  }, [user, currentConversation?.id]);

  // Fonction utilitaire pour vérifier et créer une conversation
const checkAndCreateConversation = useCallback(async (clientId, moverId) => {

  if (!clientId || !moverId) {
    const error = new Error("❌ clientId ou moverId manquant");
    console.error("[ChatContext] Erreur:", error.message, { clientId, moverId });
    throw error;
  }

  try {

    // Vérifier si une conversation existe déjà
    const existingConversation = conversations.find(
      conv =>
        (conv.clientId === clientId || conv.clientId?.id === clientId) &&
        (conv.moverId === moverId || conv.moverId?.id === moverId)
    );

    if (existingConversation) {
 
      return existingConversation;
    }


    try {

      const token = localStorage.getItem("liberty_token");

      const response = await chatService.checkClientMoverRelationship({ clientId, moverId });
    
      if (response?.hasRelationship) {

        try {
          const newConversation = await chatService.startConversation({ clientId, moverId });

       
          setConversations((prev) => {
            return [newConversation, ...prev];
          });

          return newConversation;
        } catch (startConvError) {
          console.error("❌ [ChatContext] Erreur création conversation:", {
            message: startConvError.message,
            status: startConvError.response?.status,
            data: startConvError.response?.data,
          });
          throw startConvError;
        }
      } else {
        const error = new Error(response?.message || "❌ Pas de relation valide client-déménageur");
        console.warn("[ChatContext] Avertissement:", error.message, { response });
        throw error;
      }
    } catch (checkRelError) {
      console.error("💥 [ChatContext] Erreur check relation:", {
        message: checkRelError.message,
        status: checkRelError.response?.status,
        data: checkRelError.response?.data,
        config: checkRelError.config,
      });
      throw checkRelError;
    }
  } catch (error) {
    console.error("💥 [ChatContext] Erreur globale checkAndCreateConversation:", error);
    return null;
  }
}, [conversations]);


  // Fonction pour démarrer ou récupérer une conversation
  const startConversation = useCallback(async (params) => {
    
    // Fonction interne pour gérer la création de conversation
    const executeStartConversation = async (params, loadMessagesFn) => {
      try {
        
        // Si c'est un ID de devis
        if (typeof params === 'string' || params.quoteId) {
          const quoteId = typeof params === 'string' ? params : params.quoteId;
          
          const existingConversation = conversations.find(
            (c) => c.quoteId === quoteId || (c.quote && c.quote.id === quoteId)
          )

          if (existingConversation) {
            setCurrentConversation(existingConversation)
            await loadMessagesFn(existingConversation.id)
            return existingConversation
          }

          const conversation = await chatService.startConversation({ quoteId })
          setConversations((prev) => [conversation, ...prev])
          setCurrentConversation(conversation)
          await loadMessagesFn(conversation.id)
          return conversation
        }
        
        // Si ce sont des IDs client/déménageur
        if (params.clientId && params.moverId) {
        
          return await checkAndCreateConversation(params.clientId, params.moverId);
        }

        throw new Error("Paramètres de conversation invalides");
        
      } catch (error) {
        console.error("Error starting conversation:", error)
        showError(error.message || "Impossible de démarrer la conversation")
        throw error
      }
    };

    return executeStartConversation(params, loadMessages);
  }, [conversations, showError, checkAndCreateConversation, loadMessages, setCurrentConversation, setConversations]);

  /** ============= FETCH CONVERSATIONS ============= **/
  const fetchConversations = useCallback(async (force = false) => {
    // Wait for auth to be ready
    if (!user) {
      return [];
    }

    if (userRoleIsAdmin(user)) {
      lastDataRef.current = []
      lastFetchTimeRef.current = Date.now()
      if (isMounted.current) {
        setConversations([])
        setUnreadCount(0)
      }
      return []
    }
    
    // Use the user object directly from the hook parameter
    const currentUserId = user.id || user.user?.id;
    
    if (!currentUserId) {
      console.warn('[fetchConversations] No user ID available in user object:', user);
      return [];
    }

    const now = Date.now();
    const MIN_REFRESH_INTERVAL = 10000; // 10 seconds

    if ((!force && now - lastFetchTimeRef.current < MIN_REFRESH_INTERVAL) || inProgressRef.current) {
      return lastDataRef.current || [];
    }

    inProgressRef.current = true;

    try {
      const response = await chatService.getConversations();
      
      const conversations = Array.isArray(response) ? response : [];
      
      lastDataRef.current = conversations;
      lastFetchTimeRef.current = now;

      if (isMounted.current) {
        setConversations(conversations);

        const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unreadCount || 0), 0);
        setUnreadCount(totalUnread);
      }

      return conversations;
    } catch (error) {
      console.error('[fetchConversations] Error:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url
      });
      showError("Impossible de charger les conversations");
      return [];
    } finally {
      inProgressRef.current = false;
    }
  }, [user, showError])

  /** ============= WEBSOCKET INIT ============= **/
  const initializeWebSocket = useCallback(async () => {
    if (initInProgressRef.current || socketInitialized.current) {
      return
    }

    if (userRoleIsAdmin(user) || readAdminFromStorage()) {
      socketInitialized.current = true
      initInProgressRef.current = false
      initializingRef.current = false
      return
    }

    initInProgressRef.current = true
    initializingRef.current = true

    if (isConnected) {
      initInProgressRef.current = false
      initializingRef.current = false
      return
    }

    if (!token) {
      initInProgressRef.current = false
      initializingRef.current = false
      return
    }

    try {
      if (!cleanupRef.current) {
        const cleanupFunctions = []

        const cleanupConnect = chatService.on("connect", () => {
          setIsConnected(true)
          hasConnectedRef.current = true
          if (user?.id) fetchConversations().catch(console.error)
        })
        cleanupFunctions.push(cleanupConnect)

        const cleanupDisconnect = chatService.on("disconnect", () => {
          setIsConnected(false)
          hasConnectedRef.current = false
        })
        cleanupFunctions.push(cleanupDisconnect)

        const cleanupError = chatService.on("error", () => setIsConnected(false))
        cleanupFunctions.push(cleanupError)

        const cleanupTyping = chatService.on("typing", handleTyping)
        cleanupFunctions.push(cleanupTyping)

        const cleanupMessage = chatService.on("message", handleNewMessage)
        cleanupFunctions.push(cleanupMessage)

        cleanupRef.current = () => {
          cleanupFunctions.forEach((cleanup) => cleanup && cleanup())
          cleanupRef.current = null
        }
      }

      if (!chatService.isConnected()) {
        await chatService.connect(token)
      }

      socketInitialized.current = true
    } catch (err) {
      setIsConnected(false)

      if (!reconnectTimeoutRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null
          initializeWebSocket()
        }, 5000)
      }
    } finally {
      initInProgressRef.current = false
      initializingRef.current = false
    }
  }, [user, user?.id, token, isConnected, handleTyping, handleNewMessage, fetchConversations])

  /** ============= EFFECTS ============= **/
  const getUserFromLocalStorage = useCallback(() => {
    try {
      const authData = localStorage.getItem("user")
      if (authData) {
        const parsed = JSON.parse(authData)
        return parsed.user || parsed
      }
      return null
    } catch (error) {
      console.error("Error parsing user data from localStorage:", error)
      return null
    }
  }, [])

  useEffect(() => {
    const currentUser = user?.id ? user : getUserFromLocalStorage()
    const hasUser = !!currentUser?.id

    if (hasUser && token && !isConnected && !socketInitialized.current) {
      if (!user?.id && currentUser) setUser(currentUser)
      initializeWebSocket().catch(console.error)
    }
  }, [user?.id, token, isConnected, initializeWebSocket, setUser, getUserFromLocalStorage]);

  useEffect(() => {
    if (!token || !user?.id) {
      socketInitialized.current = false
    }
  }, [token, user?.id])

  useEffect(() => {
    if (!userRoleIsAdmin(user)) return
    try {
      chatService.stopWebSocket()
    } catch (_) {}
    socketInitialized.current = true
    setIsConnected(false)
  }, [user?.id, user?.role, user?.user_type])

  useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
      if (cleanupRef.current) {
        cleanupRef.current()
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current)
        reconnectTimeoutRef.current = null
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current)
        typingTimeoutRef.current = null
      }
      try {
        chatService.stopWebSocket()
      } catch (_) {
        /* ignore */
      }
      socketInitialized.current = false
    }
  }, [])

  /** ============= CONTEXT VALUE ============= **/
  const value = {
    conversations,
    currentConversation,
    messages,
    isTyping,
    typingUserId,
    unreadCount,
    isConnected,
    loadMessages,
    startConversation,
    fetchConversations,
    sendMessage: async (content) => {
      if (!currentConversation) {
        console.error('No active conversation');
        throw new Error("No active conversation");
      }
    
      
      // Create an optimistic update with a temporary ID
      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        conversationId: currentConversation.id,
        content,
        senderId: user?.id,
        isRead: false,
        createdAt: new Date().toISOString(),
        isOptimistic: true
      };
      
      // Update the UI immediately
      setMessages(prev => [optimisticMessage, ...prev]);
      
      try {
        // Send the message through the WebSocket
        await chatService.sendChatMessage(currentConversation.id, content);
        
        // Return the optimistic message
        return optimisticMessage;
        
      } catch (error) {
        console.error('Error sending message:', error);
        // Remove the optimistic update if it fails
        setMessages(prev => prev.filter(msg => !msg.isOptimistic || msg.id !== tempId));
        throw error;
      }
    },
    markAsRead,
    sendTyping: (isTyping) =>
      currentConversation && chatService.sendTyping(currentConversation.id, isTyping),
    setCurrentConversation: (conv) => {
      if (!conv) {
        setCurrentConversation(null);
        return;
      }
      
      // Determine the receiver ID based on the current user
      const currentUserId = user?.id;
      let receiverId = null;
      
      if (conv.clientId && (conv.clientId.id === currentUserId || conv.clientId === currentUserId)) {
        // If current user is the client, receiver is the mover
        receiverId = conv.moverId?.id || conv.moverId;
      } else if (conv.moverId && (conv.moverId.id === currentUserId || conv.moverId === currentUserId)) {
        // If current user is the mover, receiver is the client
        receiverId = conv.clientId?.id || conv.clientId;
      }
      
      // Update conversation with receiverId
      const updatedConversation = {
        ...conv,
        receiverId: receiverId || conv.receiverId
      };
      
   
      
      setCurrentConversation(updatedConversation);
      if (updatedConversation.id) {
        loadMessages(updatedConversation.id).catch(console.error);
      }
    },
  }

  // S'assurer que toutes les valeurs nécessaires sont incluses dans le contexte
  const contextValue = {
    ...value,
    showError: showError || defaultContextValue.showError,
    isLoading,
    setIsLoading
  }

  return (
    <ChatContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </ChatContext.Provider>
  )
}

// Hook personnalisé
function useChat() {
  const context = useContext(ChatContext)
  if (!context) {
    throw new Error("useChat must be used within a ChatProvider")
  }
  return context
}

export { useChat }
export default ChatContext
